import { CancellablePromise, Cancellation } from "real-cancellable-promise";

import { CustomHeader, HttpStatusCode, ResponseError, UnexpectedResponseError } from "@/api";
import { config } from "@/config";
import { ApiHelper } from "@/helpers";

function request<T>(path: string, options: RequestInit): CancellablePromise<T> {
  const controller = new AbortController();

  const promise = fetch(
    new Request(`${config.api.pathPrefix}${path}`, {
      signal: controller.signal,
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...options.headers
      }
    })
  )
    .then((response) => {
      if (response.ok) {
        return parseResponse<T>(response);
      }

      return parseError<T>(response);
    })
    .catch((e) => {
      if (e.name === "AbortError") {
        throw new Cancellation();
      }

      throw e;
    });

  return new CancellablePromise<T>(promise, () => controller.abort());
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === HttpStatusCode.RESET_CONTENT) {
    // We simply reload the page, assuming that nothing that needs to be updated
    // is kept in local or session storage. A flush parameter could be supported,
    // but we would need to only flush the relevant parts of the cache.
    window.location.reload();
    throw new Cancellation();
  }

  if (response.status === HttpStatusCode.NO_CONTENT) {
    return {} as T;
  }

  const modal = response.headers.get(CustomHeader.LemmaApiModal);
  if (modal) {
    window.location.href = `${window.location.protocol}${window.location.hostname}#/?modals=${modal}`;
    throw new Cancellation();
  }

  try {
    return await response.json();
  } catch (e) {
    throw new UnexpectedResponseError("Invalid JSON");
  }
}

async function parseError<T>(response: Response): Promise<T> {
  if (ApiHelper.isRedirect(response.status)) {
    const url = response.headers.get(CustomHeader.LemmaApiLocation);
    if (url) {
      window.location.href = url;
      throw new Cancellation();
    }
  }

  let content = { code: "", message: "" };

  try {
    content = await response.json();
  } catch (e) {
    throw new UnexpectedResponseError("Invalid JSON");
  }

  throw new ResponseError(content.code, content.message);
}

function buildQuery(data: Record<string, unknown>): string {
  const params = new URLSearchParams();

  const addParam = (key: string, value: unknown) => {
    if (typeof value === "boolean") {
      value = value ? 1 : 0;
    }
    if (value !== undefined && value !== null) {
      params.append(`${key}`, `${value}`);
    }
  };

  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((val) => {
        addParam(`${key}[]`, val);
      });
    } else {
      addParam(key, value);
    }
  });

  return params.toString();
}

function buildBody(data: unknown): string {
  return JSON.stringify(data);
}

export function get<T>(path: string, data?: Record<string, unknown>, options?: RequestInit): CancellablePromise<T> {
  if (data) {
    path = `${path}?${buildQuery(data)}`;
  }

  return request<T>(path, {
    method: "GET",
    ...options
  });
}

export function post<T>(path: string, data?: unknown, options?: RequestInit): CancellablePromise<T> {
  return request<T>(path, {
    method: "POST",
    body: buildBody(data),
    ...options
  });
}

export function put<T>(path: string, data: unknown, options?: RequestInit): CancellablePromise<T> {
  return request<T>(path, {
    method: "PUT",
    body: buildBody(data),
    ...options
  });
}

export function patch<T>(path: string, data: unknown, options?: RequestInit): CancellablePromise<T> {
  return request<T>(path, {
    method: "PATCH",
    body: buildBody(data),
    ...options
  });
}

export function del<T>(path: string, data?: RequestInit): CancellablePromise<T> {
  return request<T>(path, {
    method: "DELETE",
    ...data
  });
}

export { CancellablePromise, Cancellation };
